import { FeedbackError } from '@/components/Feedback';
import Lightbox from '@/components/Lightbox';
import { PhotoAnnotatorModal } from '@/components_v2/PhotoAnnotator';
import { formularyStorage } from '@/modules/shared/adapters/CloudStorage';
import { blobToFile } from '@/utils/blobToFile';
import { readFile } from '@/utils/readFile';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ImagePreview from './components/ImagePreview';
import UploadInput from './components/UploadInput';
import { DropzoneState, GalleryUploadProps } from './interfaces';

export const GalleryUpload: React.FC<GalleryUploadProps> = (props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    onChange,
    urls,
    setUrls,
    withPortal,
    disabled,
    maxPictures,
    withLimit,
  } = props;

  const [status, setStatus] = useState<DropzoneState>('EMPTY');
  const [progress, setProgress] = useState<number>();
  const [annotationQueue, setAnnotationQueue] = useState<File[]>([]);
  const [currentFileUrl, setCurrentFileUrl] = useState<string | null>(null);
  const { t } = useTranslation();
  const modalSize = useBreakpointValue({ base: 'full', lg: '2xl' });
  const currentFile = annotationQueue[0] ?? null;

  useEffect(() => {
    if (!currentFile) {
      setCurrentFileUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(currentFile);
    setCurrentFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [currentFile]);

  const setUploadProgress = useCallback((prog: number) => {
    setProgress(prog);
  }, []);

  const onUploadSuccessful = useCallback(
    (url: string) => {
      setStatus('UPLOADED');
      setUrls((prev) => {
        const newUrls = [...prev, url];
        onChange(newUrls);
        return newUrls;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange],
  );

  const onRemove = React.useCallback(
    (index: number) => {
      setUrls((prev) => {
        const newUrls = prev
          .slice(0, index)
          .concat(prev.slice(index + 1, prev.length));
        onChange(newUrls);
        return newUrls;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange],
  );

  const onLightbox = React.useCallback(
    (index: number) => {
      Lightbox.openLightbox(urls, index);
    },
    [urls],
  );

  const uploadFile = useCallback(
    (file: File) => {
      try {
        setStatus('UPLOADING');
        formularyStorage.uploadFile({
          file,
          setProgress: setUploadProgress,
          setFileDownloadUrl: (url) => onUploadSuccessful(url),
        });
      } catch (e) {
        if (e instanceof Error) {
          FeedbackError({
            mainText: t(`common.error`),
            subText: t(`error.${e.message}`),
          });
          setStatus('EMPTY');
        }
      }
    },
    [t, setUploadProgress, onUploadSuccessful],
  );

  // Imagens soltas/selecionadas passam pelo preview de anotação antes de subir.
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setAnnotationQueue((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const handleAnnotationConfirm = useCallback(
    async (dataUrl: string | null) => {
      if (!currentFile) return;
      if (dataUrl) {
        const blob = await readFile(dataUrl);
        const file = await blobToFile(blob, currentFile.name);
        uploadFile(file);
      } else {
        uploadFile(currentFile);
      }
      setAnnotationQueue((prev) => prev.slice(1));
    },
    [currentFile, uploadFile],
  );

  const handleAnnotationCancel = useCallback(() => {
    setAnnotationQueue((prev) => prev.slice(1));
  }, []);

  return (
    <>
      <Button colorScheme="brand" onClick={onOpen}>
        Anexar imagens
      </Button>

      <Modal size={modalSize} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Envie imagens</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack>
              <UploadInput
                isUploading={status === 'UPLOADING'}
                progress={progress}
                onDrop={onDrop}
                withPortal={withPortal}
                disabled={disabled}
                maxPictures={maxPictures}
                urls={urls}
                withLimit={withLimit}
              />
              {disabled && (
                <Text color="red.500">
                  Você atingiu o limite de imagens para envio.
                </Text>
              )}
              <Text>Imagens na nuvem:</Text>
              <Wrap w="full">
                {urls.map((p, index) => (
                  <WrapItem key={`gallery_img_${index}`}>
                    <ImagePreview
                      url={p}
                      onRemove={() => onRemove(index)}
                      onOpen={() => onLightbox(index)}
                    />
                  </WrapItem>
                ))}
              </Wrap>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <PhotoAnnotatorModal
        isOpen={!!currentFile}
        photoSrc={currentFileUrl}
        onClose={handleAnnotationCancel}
        onConfirm={handleAnnotationConfirm}
      />
    </>
  );
};
